/**
 * Med Access Landing Page – /med-access/:token
 *
 * Public page opened by a doctor after scanning a MED ACCESS QR code.
 *
 * Flow:
 *  Step 1 – Token info shown (expiry, traveler name hidden)
 *  Step 2 – Doctor enters name + email + logs in (redirect to login with return URL)
 *  Step 3 – After login, claim is submitted → traveler gets push notification
 *  Step 4 – Poll / SSE until traveler approves → show health data
 */
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";
import { useAuthStore } from "@/stores/authStore";
import { useI18nStore } from "@/stores/i18nStore";
import type { Translation } from "@/types/i18n";
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Lock,
  Mail,
  RefreshCw,
  Shield,
  Stethoscope,
  User,
  XCircle,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { EmergencyProfileData } from "@/types/emergency";

// ── Types ─────────────────────────────────────────────────────────────────────

type Step =
  | "loading"
  | "info"
  | "claim"
  | "pending"
  | "granted"
  | "denied"
  | "expired"
  | "error";

interface TokenInfo {
  status: "PENDING" | "GRANTED" | "DENIED" | "REVOKED";
  expiresAt: string;
  claimed: boolean;
}

interface ClaimForm {
  doctorName: string;
  doctorEmail: string;
}

interface HealthData {
  // /med-access/data returns the priority-1/2/3 aggregates verbatim. Only P1
  // is rendered field by field; P2 and P3 are dumped as JSON below.
  priority1: EmergencyProfileData;
  priority2: Record<string, unknown>;
  priority3: Record<string, unknown>;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const MedAccessPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();

  const [step, setStep] = useState<Step>("loading");
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [form, setForm] = useState<ClaimForm>({
    doctorName: user?.firstName
      ? `${user.firstName} ${user.lastName ?? ""}`.trim()
      : "",
    doctorEmail: user?.email ?? "",
  });
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [errMsg, setErrMsg] = useState("");
  const [claiming, setClaiming] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Load token info ─────────────────────────────────────────────────────────
  const loadToken = useCallback(async () => {
    if (!token) {
      setStep("error");
      return;
    }
    try {
      const res = await apiClient.get(`/med-access/${token}`);
      const info: TokenInfo = res.data.data;
      setTokenInfo(info);

      if (info.status === "GRANTED") {
        // Already approved – try to load data if we are the doctor
        if (isAuthenticated) {
          await loadData();
          return;
        }
        setStep("granted");
        return;
      }
      if (info.status === "DENIED") {
        setStep("denied");
        return;
      }
      if (info.status === "REVOKED") {
        setStep("expired");
        return;
      }
      if (new Date(info.expiresAt) < new Date()) {
        setStep("expired");
        return;
      }
      if (info.claimed) {
        setStep("pending");
        startPolling();
        return;
      }

      setStep("info");
    } catch (e: unknown) {
      const code = (
        e as { response?: { data?: { error?: { code?: string } } } }
      )?.response?.data?.error?.code;
      setStep(code === "EXPIRED" ? "expired" : "error");
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    loadToken();
  }, [loadToken]);

  // Pre-fill form when user logs in
  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        doctorName:
          prev.doctorName ||
          `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
        doctorEmail: prev.doctorEmail || user.email || "",
      }));
    }
  }, [user]);

  // ── Polling ─────────────────────────────────────────────────────────────────
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiClient.get(`/med-access/${token}`);
        const info: TokenInfo = res.data.data;
        if (info.status === "GRANTED") {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          await loadData();
        } else if (info.status === "DENIED") {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setStep("denied");
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
  }, [token]);

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    [],
  );

  // ── Load health data ────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    try {
      const res = await apiClient.get(`/med-access/${token}/data`);
      setHealthData(res.data.data);
      setStep("granted");
    } catch {
      setStep("pending");
      startPolling();
    }
  }, [token, startPolling]);

  // ── Claim token ─────────────────────────────────────────────────────────────
  const handleClaim = async () => {
    if (!form.doctorName.trim() || !form.doctorEmail.trim()) {
      setErrMsg(t.cardxClaimNameEmailRequired);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.doctorEmail)) {
      setErrMsg(t.cardxClaimEmailInvalid);
      return;
    }
    setClaiming(true);
    setErrMsg("");
    try {
      await apiClient.put(`/med-access/${token}/claim`, {
        doctorName: form.doctorName.trim(),
        doctorEmail: form.doctorEmail.trim(),
      });
      setStep("pending");
      startPolling();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message || t.cardxClaimFailed;
      setErrMsg(msg);
    } finally {
      setClaiming(false);
    }
  };

  const fmtExpiry = (iso: string) => fmt.dateTime(iso, "medium");

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo strip */}
        <div className="flex justify-center mb-6">
          <img
            src="/images/healthpass_logo.svg"
            alt="HealthPass"
            className="h-8 w-auto opacity-80"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header band */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Stethoscope className="w-5 h-5" />
              </div>
              <div>
                <h1 className="font-bold text-lg">{t.cardxMedRequestTitle}</h1>
                <p className="text-blue-100 text-sm">
                  {t.cardxMedRequestSubtitle}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* ── LOADING ── */}
            {step === "loading" && (
              <Centered>
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                <p className="text-gray-500 text-sm mt-3">
                  {t.cardxVerifyingToken}
                </p>
              </Centered>
            )}

            {/* ── INFO – introduce the request ── */}
            {step === "info" && tokenInfo && (
              <div className="space-y-5">
                <InfoBanner icon={Shield} color="blue">
                  {t.cardxMedInfoBanner}
                </InfoBanner>

                <dl className="text-sm space-y-2">
                  <Row
                    label={t.cardxRowValidUntil}
                    value={fmtExpiry(tokenInfo.expiresAt)}
                  />
                  <Row
                    label={t.cardxRowAccessLevel}
                    value={t.cardxRowAccessLevelValue}
                  />
                  <Row
                    label={t.cardxRowConsentRequired}
                    value={t.cardxRowConsentRequiredValue}
                  />
                </dl>

                {!isAuthenticated ? (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500">
                      {t.cardxNeedAccount}
                    </p>
                    <button
                      onClick={() =>
                        navigate(
                          `/traveler/login?returnTo=/med-access/${token}`,
                        )
                      }
                      className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
                    >
                      <Lock className="w-4 h-4" /> {t.cardxLoginToContinue}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setStep("claim")}
                    className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors"
                  >
                    {t.cardxRequestAccess}
                  </button>
                )}
              </div>
            )}

            {/* ── CLAIM – enter doctor details ── */}
            {step === "claim" && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">{t.cardxClaimIntro}</p>
                <Field label={t.cardxYourFullName} icon={User}>
                  <input
                    type="text"
                    value={form.doctorName}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, doctorName: e.target.value }))
                    }
                    placeholder={t.cardxDoctorNamePlaceholder}
                    className={inputCls}
                  />
                </Field>
                <Field label={t.cardxYourEmail} icon={Mail}>
                  <input
                    type="email"
                    value={form.doctorEmail}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, doctorEmail: e.target.value }))
                    }
                    placeholder={t.cardxDoctorEmailPlaceholder}
                    className={inputCls}
                  />
                </Field>
                {errMsg && (
                  <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">
                    {errMsg}
                  </p>
                )}
                <button
                  onClick={handleClaim}
                  disabled={claiming}
                  className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {claiming ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Stethoscope className="w-4 h-4" />
                  )}
                  {t.cardxSendAccessRequest}
                </button>
              </div>
            )}

            {/* ── PENDING – waiting for traveler consent ── */}
            {step === "pending" && (
              <div className="space-y-4 text-center">
                <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
                  <Clock className="w-8 h-8 text-amber-500" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900">
                    {t.cardxWaitingApproval}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {t.cardxWaitingApprovalText}
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  {t.cardxCheckingEvery5s}
                </div>
              </div>
            )}

            {/* ── GRANTED – show health record ── */}
            {step === "granted" && (
              <div className="space-y-4">
                <InfoBanner icon={CheckCircle2} color="green">
                  {t.cardxAccessGrantedBanner}
                </InfoBanner>
                {healthData ? (
                  <HealthDataView data={healthData} t={t} />
                ) : (
                  <div className="text-center py-6">
                    <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto" />
                    <p className="text-xs text-gray-400 mt-2">
                      {t.cardxLoadingRecords}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ── DENIED ── */}
            {step === "denied" && (
              <Centered>
                <XCircle className="w-12 h-12 text-red-400" />
                <h2 className="font-bold text-gray-900 mt-3">
                  {t.cardxAccessDeniedTitle}
                </h2>
                <p className="text-sm text-gray-500 mt-1 text-center">
                  {t.cardxAccessDeniedText}
                </p>
              </Centered>
            )}

            {/* ── EXPIRED ── */}
            {step === "expired" && (
              <Centered>
                <AlertCircle className="w-12 h-12 text-gray-400" />
                <h2 className="font-bold text-gray-900 mt-3">
                  {t.cardxLinkExpiredTitle}
                </h2>
                <p className="text-sm text-gray-500 mt-1 text-center">
                  {t.cardxLinkExpiredText}
                </p>
              </Centered>
            )}

            {/* ── ERROR ── */}
            {step === "error" && (
              <Centered>
                <AlertCircle className="w-12 h-12 text-red-400" />
                <h2 className="font-bold text-gray-900 mt-3">
                  {t.cardxInvalidLinkTitle}
                </h2>
                <p className="text-sm text-gray-500 mt-1 text-center">
                  {t.cardxInvalidLinkText}
                </p>
              </Centered>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          {t.cardxMedFooter}
        </p>
      </div>
    </div>
  );
};

// ── Health Data View ──────────────────────────────────────────────────────────

/** Criticality codes stay as stored; only the badge text is translated. */
function criticalityLabel(code: string, t: Translation): string {
  if (code === "high") return t.cardxCritHigh;
  if (code === "low") return t.cardxCritLow;
  if (code === "unable-to-assess") return t.cardxCritUnableToAssess;
  return code;
}

const HealthDataView: React.FC<{ data: HealthData; t: Translation }> = ({
  data,
  t,
}) => {
  const p1 = data.priority1;
  const p2 = data.priority2 as Record<string, unknown>;

  // These three casts previously asserted a payload the API does not produce -
  // `substance`/`severity` on allergies, `name` on medications, and a
  // top-level `bloodType` that actually lives under `patient`. Every one of
  // them rendered blank on a page a doctor reads.
  const allergies = p1?.allergies ?? [];
  const medications = p1?.medications ?? [];
  const bloodType = p1?.patient?.bloodType ?? undefined;

  return (
    <div className="space-y-4 text-sm">
      {bloodType && (
        <Section title={t.cardxBloodType} icon={Activity}>
          <span className="font-bold text-red-600 text-lg">{bloodType}</span>
        </Section>
      )}

      {allergies.length > 0 && (
        <Section
          title={t.cardxAllergiesCount.replace("{n}", String(allergies.length))}
          icon={AlertCircle}
        >
          <ul className="space-y-1">
            {allergies.map((a, i) => (
              <li key={i} className="flex items-center justify-between">
                <span>{a.allergen || t.unknown}</span>
                {a.criticality && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      a.criticality === "high"
                        ? "bg-red-100 text-red-700"
                        : a.criticality === "low"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-orange-100 text-orange-700"
                    }`}
                  >
                    {criticalityLabel(a.criticality, t)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {medications.length > 0 && (
        <Section
          title={t.cardxCriticalMedsCount.replace(
            "{n}",
            String(medications.length),
          )}
          icon={Shield}
        >
          <ul className="space-y-1">
            {medications.map((m, i) => (
              <li key={i} className="flex items-center justify-between">
                <span>{m.medicationName || t.unknown}</span>
                {m.dosage && (
                  <span className="text-gray-500 text-xs">{m.dosage}</span>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {Object.keys(p2 ?? {}).length > 0 && (
        <details className="rounded-lg border border-gray-200">
          <summary className="px-4 py-2.5 text-xs font-semibold text-gray-600 cursor-pointer select-none">
            {t.cardxExtendedProfile}
          </summary>
          <div className="px-4 pb-3 pt-1">
            <pre className="text-xs text-gray-500 whitespace-pre-wrap break-all max-h-60 overflow-y-auto">
              {JSON.stringify(p2, null, 2)}
            </pre>
          </div>
        </details>
      )}
    </div>
  );
};

// ── Small helpers ─────────────────────────────────────────────────────────────

const inputCls =
  "w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-col items-center py-6">{children}</div>
);

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between">
    <dt className="text-gray-500">{label}</dt>
    <dd className="font-medium text-gray-800">{value}</dd>
  </div>
);

const InfoBanner: React.FC<{
  icon: React.FC<{ className?: string }>;
  color: "blue" | "green" | "amber";
  children: React.ReactNode;
}> = ({ icon: Icon, color, children }) => {
  const cls = {
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
  }[color];
  return (
    <div
      className={`flex items-start gap-2.5 rounded-lg border px-4 py-3 text-sm ${cls}`}
    >
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
};

const Section: React.FC<{
  title: string;
  icon: React.FC<{ className?: string }>;
  children: React.ReactNode;
}> = ({ title, icon: Icon, children }) => (
  <div className="rounded-lg border border-gray-200 overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
      <Icon className="w-3.5 h-3.5 text-gray-500" />
      <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
        {title}
      </span>
    </div>
    <div className="px-4 py-3">{children}</div>
  </div>
);

const Field: React.FC<{
  label: string;
  icon: React.FC<{ className?: string }>;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      {label}
    </label>
    {children}
  </div>
);
